import { Form, FormikProvider, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';

import { useErrorBoundary, useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Button,
	Combo,
	FlexGap,
	GraphQLErrorMessage,
	Heading,
	Heading3,
	OrderedMultiSelect,
	QuillText,
	SettingsSection,
	Spinner,
	tokens,
	useToasts,
} from '@treams/treams-ui';

import DetailsTableForm from '../../../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from '../../../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';

function CareerEditor(props: { career?: graphql.Career }) {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const history = useHistory();
	const { localize } = useLocalize();

	const [error, attempt, reset] = useErrorBoundary();

	const [create, { loading: createLoading }] = graphql.useCreateCareerMutation();
	const [update, { loading: updateLoading }] = graphql.useUpdateCareerMutation();

	const [fetchExpertises, { data: expertisesData, error: expertisesError }] =
		graphql.usePaginatedExpertisesLazyQuery({
			variables: {
				isArchived: false,
				first: 1000,
			},
		});

	const [fetchJobTitles, { data: jobTitlesData, error: jobTitlesError }] =
		graphql.useJobTitlesLazyQuery({
			variables: {
				isArchived: false,
				first: 1000,
			},
		});

	const loading = createLoading || updateLoading;

	const formik = useFormik({
		initialValues: {
			title: props.career?.title ?? [],
			expertiseID: props.career?.expertise.id ?? null,
			jobTitleIDs: props.career?.jobTitles.map((j) => j.id) ?? [],
		},
		onSubmit: () => onUpdate(),
	});

	const mappedExpertises =
		expertisesData?.paginatedExpertises.edges.map((edge) => {
			return {
				title: edge.node.title[0].text,
				value: edge.node.id,
			};
		}) ?? [];

	const [createExpertise, { error: createExpertiseError }] = graphql.useCreateExpertiseMutation();

	const submitExpertise = async (value: string) => {
		const expertise = await createExpertise({
			variables: {
				input: {
					title: [
						{
							languageID: localStorage.getItem('language') ?? 'nl',
							text: value,
						},
					],
				},
			},
		});
		await formik.setFieldValue('expertiseID', expertise.data?.createExpertise.id);
	};

	const updateExpertise = (choice: { custom: string } | { value: string } | undefined) => {
		if (choice && 'value' in choice) {
			return formik.setFieldValue('expertiseID', choice.value);
		}

		if (choice && 'custom' in choice) {
			return submitExpertise(choice.custom);
		}
	};

	const expertiseError = expertisesError ?? createExpertiseError ?? null;

	const mappedJobTitles =
		jobTitlesData?.jobTitles.edges
			.map((edge) => {
				return {
					title: localize(edge.node.title).text,
					value: edge.node.id,
					context: (
						<>
							<Heading3 mb={2}>{localize(edge.node.title).text}</Heading3>
							<QuillText value={localize(edge.node.description ?? [])?.text} />
						</>
					),
				};
			})
			.sort((a, b) => a.title.localeCompare(b.title)) ?? [];

	const selectedJobTitles = props.career?.jobTitles.map((jobTitle) => {
		return {
			title: jobTitle.title[0].text,
			value: jobTitle.id,
		};
	});

	const editOrCreate = async (input: graphql.CareerCreateInput) => {
		if (props.career) {
			await update({
				variables: {
					input: {
						id: props.career.id,
						...input,
					},
				},
			});
			return;
		}

		const { data } = await create({
			variables: {
				input: {
					...input,
				},
			},
		});

		if (data) {
			history.push(`/settings/jobs/growth-path/${data.createCareer.id}`);
		}
	};

	const onUpdate = attempt(async () => {
		reset();

		const input = {
			title: formik.values.title.map((string) => ({
				languageID: string.languageID,
				text: string.text,
			})),
			expertiseID: formik.values.expertiseID ?? '',
			jobTitleIDs: formik.values.jobTitleIDs ?? [''],
		};

		await editOrCreate(input);

		addToast(t('common.settings.careers.edit.success'), {
			appearance: 'success',
			autoDismiss: true,
		});
	});

	const valid = formik.values.title.some((translation) => translation.text.length > 0);

	return (
		<FlexGap flexDirection="column" gap={tokens.gap.page} width="100%">
			<Heading mb={0}>
				{t(`common.settings.careers.${props.career ? 'edit' : 'create'}.title`)}
			</Heading>
			<GraphQLErrorMessage error={error} />
			<FormikProvider value={formik}>
				<SettingsSection first title={t('common.settings.careers.edit.details')}>
					<Form>
						<DetailsTableForm
							formValues={[
								{
									label: t('common.settings.careers.field.title'),
									name: 'title',
									type: DetailsTableFormValueTypes.Multilingual,
								},
							]}
						/>
					</Form>
				</SettingsSection>

				<SettingsSection
					title={t('common.settings.careers.field.jobTitle.title')}
					description={t('common.settings.careers.field.jobTitle.description')}
				>
					{jobTitlesError && <GraphQLErrorMessage error={jobTitlesError.graphQLErrors[0]} />}
					<Box width={300}>
						<OrderedMultiSelect
							fetch={fetchJobTitles}
							placeholder={t('common.settings.careers.field.jobTitle.placeholder')}
							options={mappedJobTitles}
							{...(selectedJobTitles ? { selectedOptions: selectedJobTitles } : {})}
							onUpdate={(item) =>
								formik.setFieldValue(
									'jobTitleIDs',
									item.map((i) => i.value),
								)
							}
						/>
					</Box>
				</SettingsSection>

				<SettingsSection
					title={t('common.settings.careers.field.expertise.title')}
					description={t('common.settings.careers.field.expertise.description')}
				>
					{expertiseError && <GraphQLErrorMessage error={expertiseError.graphQLErrors[0]} />}
					<Box width={300}>
						<Combo
							fetch={fetchExpertises}
							placeholder={t('common.settings.careers.field.expertise.placeholder')}
							onChange={(choice) => updateExpertise(choice)}
							{...(props.career
								? { preSelectedValue: localize(props.career.expertise.title!).text }
								: {})}
							options={mappedExpertises}
						/>
					</Box>
				</SettingsSection>
			</FormikProvider>

			<Flex justifyContent="flex-end">
				<Button
					title={t('common.save')}
					onClick={() => formik.submitForm()}
					loading={loading}
					disabled={!valid}
				/>
			</Flex>
		</FlexGap>
	);
}

function CareerEditorID(props: { id: string }) {
	const { data, error, loading } = graphql.useCareerQuery({
		variables: { id: props.id },
	});

	if (loading) {
		return (
			<Flex my={4} justifyContent="center">
				<Spinner />
			</Flex>
		);
	} else if (error) {
		return <GraphQLErrorMessage error={error.graphQLErrors[0]} />;
	} else {
		return <CareerEditor career={data!.career as graphql.Career} />;
	}
}

export { CareerEditor as CareerCreator };

export default CareerEditorID;
